/**
 * sl-automation-refactor.4: AutomationAutoSaveService
 *
 * Handles debounced auto-saving of automation configs to backend.
 * Provides seamless persistence without explicit save buttons.
 *
 * SOLID:
 * - S: Single Responsibility - Only handles auto-save logic
 * - D: Depends on AutomationConfigApiService abstraction
 */

import { Service } from '@toeverything/infra';
import { LiveData } from '@toeverything/infra';

import type { AutomationConfig } from './automation';
import type { ConfigInput } from './automation-config-api';
import { AutomationConfigApiService } from './automation-config-api';

// =============================================================================
// Types
// =============================================================================

export interface SaveContext {
  blockId: string;
  workspaceId: string;
  docId: string;
}

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface PendingSave {
  context: SaveContext;
  config: AutomationConfig;
  timeoutId: ReturnType<typeof setTimeout>;
}

// =============================================================================
// Constants
// =============================================================================

/** Debounce delay in milliseconds */
const DEBOUNCE_DELAY = 500;

/** Time to show "saved" status before returning to idle */
const SAVED_STATUS_DURATION = 2000;

// =============================================================================
// Service
// =============================================================================

export class AutomationAutoSaveService extends Service {
  /**
   * Current save status per block
   */
  private readonly statusMap = new Map<string, LiveData<SaveStatus>>();

  /**
   * Pending saves (debounced)
   */
  private readonly pendingSaves = new Map<string, PendingSave>();

  constructor(private readonly configApi: AutomationConfigApiService) {
    super();
  }

  /**
   * Get reactive save status for a block
   */
  getStatus$(blockId: string): LiveData<SaveStatus> {
    let status$ = this.statusMap.get(blockId);
    if (!status$) {
      status$ = new LiveData<SaveStatus>('idle');
      this.statusMap.set(blockId, status$);
    }
    return status$;
  }

  /**
   * Schedule a debounced save for a config.
   * Call this whenever the config changes.
   */
  scheduleSave(context: SaveContext, config: AutomationConfig): void {
    const { blockId } = context;

    // Cancel any existing pending save
    const existing = this.pendingSaves.get(blockId);
    if (existing) {
      clearTimeout(existing.timeoutId);
    }

    // Update status to pending
    this.getStatus$(blockId).next('pending');

    // Schedule new save
    const timeoutId = setTimeout(() => {
      this.executeSave(context, config).catch(err => {
        console.error('[AutoSave] Failed to save:', err);
      });
    }, DEBOUNCE_DELAY);

    this.pendingSaves.set(blockId, {
      context,
      config,
      timeoutId,
    });
  }

  /**
   * Execute save immediately (bypasses debounce).
   * Useful for critical saves (e.g., before navigation).
   */
  async saveNow(context: SaveContext, config: AutomationConfig): Promise<void> {
    const { blockId } = context;

    // Cancel any pending debounced save
    const existing = this.pendingSaves.get(blockId);
    if (existing) {
      clearTimeout(existing.timeoutId);
      this.pendingSaves.delete(blockId);
    }

    await this.executeSave(context, config);
  }

  /**
   * Cancel pending save for a block.
   */
  cancelPendingSave(blockId: string): void {
    const existing = this.pendingSaves.get(blockId);
    if (existing) {
      clearTimeout(existing.timeoutId);
      this.pendingSaves.delete(blockId);
      this.getStatus$(blockId).next('idle');
    }
  }

  /**
   * Flush all pending saves immediately.
   * Call this before app close or navigation.
   */
  async flushAll(): Promise<void> {
    const saves = Array.from(this.pendingSaves.entries());

    for (const [blockId, pending] of saves) {
      clearTimeout(pending.timeoutId);
      this.pendingSaves.delete(blockId);
      await this.executeSave(pending.context, pending.config);
    }
  }

  /**
   * Check if there are any unsaved changes.
   */
  hasUnsavedChanges(): boolean {
    return this.pendingSaves.size > 0;
  }

  // ===========================================================================
  // Private Methods
  // ===========================================================================

  private async executeSave(
    context: SaveContext,
    config: AutomationConfig
  ): Promise<void> {
    const { blockId, workspaceId, docId } = context;
    const status$ = this.getStatus$(blockId);

    // Remove from pending
    this.pendingSaves.delete(blockId);

    status$.next('saving');

    try {
      // Save config
      const configInput: ConfigInput = {
        docId,
        ...(config.executionKind === 'cleanup'
          ? {
              executionKind: 'cleanup',
              sourceTableId: config.sourceTableId ?? undefined,
              sourceDocId: config.sourceDocId ?? docId,
              filterConfig: config.filterGroup,
            }
          : {}),
      };

      const savedConfig = await this.configApi.saveConfig(
        blockId,
        workspaceId,
        configInput
      );

      if (!savedConfig) {
        throw new Error('Failed to save config');
      }

      // sl-trigger-explicit: Schedule is NO LONGER auto-saved here.
      // User must explicitly enable the schedule via the toggle in ProcessTab.
      // This ensures the user has a chance to configure before activating.

      // Show saved status briefly
      status$.next('saved');
      setTimeout(() => {
        // Only reset if still showing 'saved'
        if (status$.value === 'saved') {
          status$.next('idle');
        }
      }, SAVED_STATUS_DURATION);
    } catch (error) {
      console.error('[AutoSave] Save failed:', error);
      status$.next('error');
    }
  }

  // ===========================================================================
  // Cleanup
  // ===========================================================================

  override dispose(): void {
    // Cancel all pending saves
    for (const [, pending] of this.pendingSaves) {
      clearTimeout(pending.timeoutId);
    }
    this.pendingSaves.clear();
    this.statusMap.clear();
    super.dispose();
  }
}
